'use client';

import {
    DEMO_DASHBOARD_DATA,
    DEMO_DISPERSION_DATA,
    DEMO_HISTORICAL_DATA,
    SHOT_CHART_DEMO_DATA,
} from '@/lib/constants/demo';
import {
    DispersionStats,
    StrokesGainedDashboardSchema,
    StrokesGainedHistorical,
} from '@/lib/models/strokesGained';
import { Tier, User } from '@/lib/models/user';
import { useLocation } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { createContext, ReactNode, useContext, useMemo } from 'react';

type DemoContextType = {
    isDemo: boolean;
    loaderProps: {
        isLoading: boolean;
        isFetching: boolean;
        error: Error | undefined;
    };
    userDashboard: StrokesGainedDashboardSchema;
    historical: StrokesGainedHistorical;
    dispersion: DispersionStats;
    shotChart: [number, number][];
    user: User;
};

const DemoContext = createContext<DemoContextType | null>(null);

function DemoProvider({ children }: { children: ReactNode }) {
    const location = useLocation();
    const isDemo = useMemo(() => location.pathname?.includes('/demo') ?? false, [location.pathname]);

    const roundDaysShift = useMemo(
        () => Math.abs(dayjs(DEMO_HISTORICAL_DATA.rounds[0].round_date).diff(dayjs(), 'days')),
        [],
    );

    const demoHistorical = useMemo(() => {
        return {
            rounds_logged: DEMO_HISTORICAL_DATA.rounds_logged,
            rounds: DEMO_HISTORICAL_DATA.rounds?.map(round => {
                return {
                    ...round,
                    round_date: dayjs(round.round_date).add(roundDaysShift, 'days').toISOString(),
                };
            }),
        };
    }, [roundDaysShift]);

    return (
        <DemoContext.Provider
            value={{
                isDemo,
                loaderProps: {
                    isLoading: false,
                    isFetching: false,
                    error: undefined,
                },
                user: {
                    id: 1_000_000_000,
                    email: 'demo@caddystats.golf',
                    first_name: 'Demo',
                    last_name: 'User',
                    full_name: 'Demo User',
                    is_admin: false,
                    is_individual: true,
                    teams: [],
                    active_individual_plan: {
                        active: true,
                        cancel_at_period_end: false,
                        id: 1_000_000_000,
                        end_date: '',
                        start_date: '',
                        trial_end_date: '',
                        trial_start_date: '',
                        in_free_trial_period: false,
                        tier: Tier.MASTER_INDIVIDUAL,
                        team_limit: 1,
                        is_team_plan: false,
                        managed_by_stripe: false,
                    },
                    features: {},
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                historical: demoHistorical as any,
                userDashboard: DEMO_DASHBOARD_DATA,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dispersion: DEMO_DISPERSION_DATA as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                shotChart: SHOT_CHART_DEMO_DATA as any,
            }}>
            {children}
        </DemoContext.Provider>
    );
}

function useDemoProvider() {
    const context = useContext(DemoContext);

    if (!context) {
        throw Error('No Context!');
    }
    return context;
}

export { DemoProvider, useDemoProvider };
